// @catalog F.030
// MarginsTimeline — chart anual de margen % (Bloque 5 plan dashboard_ux_ventas_margenes 2026-05-21).
// Paralelo a SalesTimeline pero las 2 líneas miden avgMarginPercentage vs previousYearMarginPercentage
// (campo opcional populado por B9 backend). Área de proyección con projectedMarginPercentage.
import { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type { MonthlyMarginsResponse } from "../../services/margins.service";

interface MarginsTimelineProps {
  data: MonthlyMarginsResponse[];
  selectedMonth: string | null;
  onMonthSelect: (monthKey: string) => void;
}

interface ChartDatum {
  month: string;
  monthShort: string;
  year: number;
  monthKey: string;
  avgMarginPercentage: number;
  previousYearMarginPercentage: number | null;
  projectedMarginPercentage: number | null;
  totalMarginAmount: number;
  isCurrentMonth: boolean;
}

const MONTH_NAMES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

const MONTH_SHORT = [
  "Ene",
  "Feb",
  "Mar",
  "Abr",
  "May",
  "Jun",
  "Jul",
  "Ago",
  "Sep",
  "Oct",
  "Nov",
  "Dic",
];

function monthIdx(name: string): number {
  const idx = MONTH_NAMES.indexOf(name.toLowerCase());
  return idx === -1 ? 0 : idx;
}

function monthKey(m: MonthlyMarginsResponse): string {
  return `${m.year}-${String(monthIdx(m.month) + 1).padStart(2, "0")}`;
}

const CURRENCY_FORMATTER = new Intl.NumberFormat("es-MX", {
  style: "currency",
  currency: "MXN",
  maximumFractionDigits: 0,
});

export function MarginsTimeline(props: MarginsTimelineProps): JSX.Element {
  const { data, selectedMonth, onMonthSelect } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const chartHeight = isMobile ? 240 : 320;

  const sorted = useMemo<ChartDatum[]>(() => {
    return [...data]
      .sort((a, b) => {
        if (a.year !== b.year) return a.year - b.year;
        return monthIdx(a.month) - monthIdx(b.month);
      })
      .map((m) => {
        const idx = monthIdx(m.month);
        const projection =
          m.isCurrentMonth &&
          m.daysElapsed !== undefined &&
          m.daysInMonth !== undefined &&
          m.daysElapsed < m.daysInMonth &&
          m.projectedMarginPercentage !== undefined
            ? m.projectedMarginPercentage
            : null;
        return {
          month: m.month,
          monthShort: MONTH_SHORT[idx] ?? m.month.slice(0, 3),
          year: m.year,
          monthKey: monthKey(m),
          avgMarginPercentage: m.avgMarginPercentage,
          previousYearMarginPercentage:
            m.previousYearMarginPercentage !== undefined
              ? m.previousYearMarginPercentage
              : null,
          projectedMarginPercentage: projection,
          totalMarginAmount: m.totalMarginAmount,
          isCurrentMonth: m.isCurrentMonth ?? false,
        };
      });
  }, [data]);

  const colors = theme.palette.chart;

  const handleClick = (payload: unknown): void => {
    if (
      payload &&
      typeof payload === "object" &&
      "activeLabel" in payload &&
      payload.activeLabel
    ) {
      const label = String(payload.activeLabel);
      const match = sorted.find((d) => d.monthShort === label);
      if (match) onMonthSelect(match.monthKey);
    }
  };

  if (sorted.length === 0) {
    return (
      <Card sx={{ width: "100%" }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            Sin datos de márgenes en el rango actual.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{ width: "100%" }}
      role="figure"
      aria-label="Timeline de margen mensual"
    >
      <CardContent sx={{ p: { xs: 1.5, md: 2.5 } }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="baseline"
          sx={{ mb: 1 }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            % margen mensual
          </Typography>
          {selectedMonth && (
            <Typography
              variant="caption"
              color="text.secondary"
              aria-live="polite"
            >
              Mes seleccionado: {selectedMonth}
            </Typography>
          )}
        </Stack>
        <Box sx={{ height: chartHeight, width: "100%" }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={sorted}
              margin={{ top: 8, right: 16, bottom: 8, left: 8 }}
              onClick={handleClick}
              style={{ cursor: "pointer" }}
            >
              <defs>
                <linearGradient
                  id="marginsTimelineAreaGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="0%"
                    stopColor={colors.primaryLine}
                    stopOpacity={0.18}
                  />
                  <stop
                    offset="100%"
                    stopColor={colors.primaryLine}
                    stopOpacity={0}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid stroke={colors.gridLine} strokeDasharray="3 3" />
              <XAxis dataKey="monthShort" tickLine={false} />
              <YAxis
                tickFormatter={(v: number) => `${v.toFixed(1)}%`}
                width={isMobile ? 50 : 70}
              />
              <Tooltip content={<MarginsTooltip />} />
              <Legend wrapperStyle={{ paddingTop: 8 }} />
              <Area
                type="monotone"
                dataKey="avgMarginPercentage"
                name="Año actual (área)"
                stroke="none"
                fill="url(#marginsTimelineAreaGradient)"
                isAnimationActive={false}
                connectNulls={false}
                legendType="none"
                tooltipType="none"
              />
              <Area
                type="monotone"
                dataKey="projectedMarginPercentage"
                name="Proyección mes en curso"
                stroke="none"
                fill={colors.projectionArea}
                isAnimationActive={false}
                connectNulls={false}
              />
              <Line
                type="monotone"
                dataKey="previousYearMarginPercentage"
                name="Año anterior"
                stroke={colors.previousYearLine}
                strokeWidth={2}
                strokeDasharray="6 4"
                dot={false}
                connectNulls={false}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="avgMarginPercentage"
                name="Año actual"
                stroke={colors.primaryLine}
                strokeWidth={3}
                dot={(dotProps: any) => {
                  const payload = dotProps.payload as ChartDatum;
                  const isSelected = payload.monthKey === selectedMonth;
                  return (
                    <circle
                      key={payload.monthKey}
                      cx={dotProps.cx}
                      cy={dotProps.cy}
                      r={isSelected ? 6 : 4}
                      fill={colors.primaryLine}
                      stroke={
                        isSelected
                          ? theme.palette.background.paper
                          : colors.primaryLine
                      }
                      strokeWidth={isSelected ? 2 : 0}
                    />
                  );
                }}
                activeDot={{ r: 7 }}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
}

interface TooltipPayloadEntry {
  dataKey: string;
  value: number | null;
  payload: ChartDatum;
}

interface TooltipProps {
  active?: boolean;
  payload?: TooltipPayloadEntry[];
}

function MarginsTooltip(props: TooltipProps): JSX.Element | null {
  const { active, payload } = props;
  if (!active || !payload || payload.length === 0) return null;
  const datum = payload[0]?.payload;
  if (!datum) return null;

  const yoyDelta =
    datum.previousYearMarginPercentage !== null
      ? datum.avgMarginPercentage - datum.previousYearMarginPercentage
      : null;

  return (
    <Card variant="outlined" sx={{ p: 1, minWidth: 220 }}>
      <Typography variant="caption" color="text.secondary">
        {datum.monthShort} {datum.year}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        % margen: {datum.avgMarginPercentage.toFixed(1)}%
      </Typography>
      <Typography variant="body2" color="text.secondary">
        $ margen total: {CURRENCY_FORMATTER.format(datum.totalMarginAmount)}
      </Typography>
      {datum.previousYearMarginPercentage !== null && (
        <Typography variant="body2" color="text.secondary">
          Año anterior: {datum.previousYearMarginPercentage.toFixed(1)}%
          {yoyDelta !== null && (
            <Typography
              component="span"
              variant="caption"
              sx={{
                ml: 0.5,
                color:
                  yoyDelta >= 0 ? "chart.growthPositive" : "chart.growthNegative",
              }}
            >
              ({yoyDelta >= 0 ? "+" : ""}
              {yoyDelta.toFixed(1)} pp)
            </Typography>
          )}
        </Typography>
      )}
      {datum.projectedMarginPercentage !== null && (
        <Typography variant="body2" color="text.secondary">
          Proyección: {datum.projectedMarginPercentage.toFixed(1)}%
        </Typography>
      )}
    </Card>
  );
}
